import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  ViewEncapsulation,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IMapBounds,
  IMapItem,
  IMapItemCounts,
} from '@kinet-open-network/shared/data-access/model';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import { ProgressBarModule } from 'primeng/progressbar';

@Component({
  selector: 'kon-optical-network-map',
  standalone: true,
  imports: [CommonModule, ProgressBarModule],
  template: `
    <div class="relative h-full">
      <p-progressBar
        [ngClass]="{
          'absolute top-0 left-0 right-0 z-[99999]': true,
          'opacity-0': !loading()
        }"
        [style]="{
          height: '4px',
        }"
        mode="indeterminate"
      />

      <div #mapContainer style="height: 100%"></div>
    </div>
  `,
  styles: `
    .map-marker-inner {
      position: relative;
    }

    .map-marker-text {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 1.25rem;
      font-weight: 600;
    }

    .map-marker-icon {
      background: white;
      border-radius: 50%;
    }

    .map-marker-circle {
      fill: none;
    }

    .map-marker-circle-success {
      stroke: var(--color-success-500);
    }

    .map-marker-circle-danger {
      stroke: var(--color-danger-500);
    }

    .map-marker-circle-warn {
      stroke: var(--color-secondary-500);
    }

    .map-marker-circle-none {
      stroke: var(--color-gray-200);
    }

    .map-marker-circle-unknown {
      stroke: var(--color-primary-500);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class OpticalNetworkMapComponent implements AfterViewInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  @ViewChild('mapContainer', { static: true })
  private mapContainer!: ElementRef<HTMLElement>;

  private map!: L.Map;
  private markerGroup?: L.MarkerClusterGroup;

  items = input<IMapItem[]>([]);
  loading = input<boolean>(false);
  labels = input.required<Record<keyof IMapItemCounts, string>>();

  load = output<IMapBounds>();
  init = output<IMapBounds>();
  itemSelect = output<IMapItem>();
  selectable = input<boolean>(false);

  selectedItem = input<{ gpsX?: number; gpsY?: number } | null>(null);

  ngAfterViewInit(): void {
    const x = this.route.snapshot.queryParams['x'] ?? 19.602;
    const y = this.route.snapshot.queryParams['y'] ?? 48.573;
    const zoom = this.route.snapshot.queryParams['zoom'] ?? 8;
    // init map
    this.map = L.map(this.mapContainer.nativeElement).setView([y, x], zoom);

    // set tile layer
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(this.map);

    // emit load on each map move
    this.map.on('moveend', (a) => {
      this.load.emit(this.getMapBounds());
      const center = a.target.getCenter();
      const zoom = a.target.getZoom();
      this.router.navigate(['.'], {
        relativeTo: this.route,
        queryParams: { x: center.lng, y: center.lat, zoom },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });

    // emit initial load
    this.init.emit(this.getMapBounds());
    this.load.emit(this.getMapBounds());
  }

  invalidateSize(): void {
    this.map?.invalidateSize();
  }

  private getMapBounds(): IMapBounds {
    const bounds = this.map.getBounds();

    return {
      n: bounds.getNorth(),
      w: bounds.getWest(),
      s: bounds.getSouth(),
      e: bounds.getEast(),
    };
  }

  private refreshMarkers(items: IMapItem[]) {
    if (this.markerGroup) {
      this.map.removeLayer(this.markerGroup);
    }

    this.markerGroup = this.createMarkerGroup(items);
    this.map.addLayer(this.markerGroup);
  }

  private createMarkerGroup(items: IMapItem[]): L.MarkerClusterGroup {
    const markerGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      maxClusterRadius: 100,
      iconCreateFunction: (cluster) => {
        const [total, counts] = this.sumChildrentMarkers(cluster);

        return this.createMarkerIcon(total, counts);
      },
    });
    items.forEach((marker) => {
      const m = L.marker<IMapItem>([marker.y, marker.x], {
        icon: this.createMarkerIcon(marker.count, marker.counts),
      });

      m.on('click', (e) => {
        if (marker.count > 1) {
          e.originalEvent.stopPropagation();
          this.map.setView([marker.y, marker.x], this.map.getZoom() + 1);
        } else if (marker.count === 1 && marker.id) {
          if (this.selectable()) {
            this.itemSelect.emit(marker);
          } else {
            this.router.navigate([marker.id], { relativeTo: this.route });
          }
        }
      });

      m.feature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [marker.x, marker.y],
        },
        properties: marker,
      };

      if (marker.count == 1) {
        m.bindTooltip(marker.name, {
          direction: 'top',
          offset: [0, -20],
          className: 'font-semibold text-sm',
        });
      } else {
        if (marker.counts) {
          m.bindTooltip(this.createCountsTooltip(marker.counts), {
            direction: 'top',
            offset: [0, -20],
          });
        }
      }

      markerGroup.addLayer(m);
    });

    markerGroup.on('clustermouseover', (layer) => {
      if (
        layer.propagatedFrom instanceof L.MarkerCluster &&
        layer.sourceTarget instanceof L.Layer
      ) {
        const [, counts] = this.sumChildrentMarkers(layer.propagatedFrom);

        if (counts) {
          layer.sourceTarget
            .bindTooltip(this.createCountsTooltip(counts), {
              direction: 'top',
              offset: [0, -20],
            })
            .openTooltip();
        }
      }
    });

    markerGroup.on('clustermouseout', (layer) => {
      if (layer.sourceTarget instanceof L.Layer) {
        layer.sourceTarget.closeTooltip();
      }
    });

    return markerGroup;
  }

  private sumChildrentMarkers(cluster: L.MarkerCluster) {
    const addCounts = (a?: number, b?: number) => {
      return b !== undefined ? (a ?? 0) + b : a;
    };
    return cluster.getAllChildMarkers().reduce(
      ([total, counts], marker) => {
        if (this.isOpticalMarker(marker)) {
          const item = marker.feature?.properties;

          total = total + (item?.count ?? 0);

          if (!item?.counts) {
            return [total, undefined] as [number, IMapItemCounts | undefined];
          } else {
            if (!counts) {
              counts = {};
            }
            counts.success = addCounts(counts.success, item.counts.success);
            counts.danger = addCounts(counts.danger, item.counts.danger);
            counts.warn = addCounts(counts.warn, item.counts.warn);
            counts.none = addCounts(counts.none, item.counts.none);
          }

          return [total, counts] as [number, IMapItemCounts | undefined];
        }

        return [total, counts] as [number, IMapItemCounts | undefined];
      },
      [0, undefined] as [number, IMapItemCounts | undefined],
    );
  }

  private createCountsTooltip(counts: IMapItemCounts) {
    const countsOptions: Record<
      keyof IMapItemCounts,
      {
        label: string;
        color: string;
        value?: number;
      }
    > = {
      success: {
        label: this.labels().success,
        color: 'bg-success-500',
        value: counts.success,
      },
      danger: {
        label: this.labels().danger,
        color: 'bg-danger-500',
        value: counts.danger,
      },
      warn: {
        label: this.labels().warn,
        color: 'bg-secondary-500',
        value: counts.warn,
      },
      none: {
        label: this.labels().none,
        color: 'bg-gray-200',
        value: counts.none,
      },
    };

    return `
      <div class="text-sm">
        ${Object.values(countsOptions)
          .filter(({ value }) => value !== undefined)
          .map(({ label, color, value }) => {
            return `
            <div class="flex items-center justify-start space-x-1">
              <div class="h-3 w-3 rounded-full ${color}"></div>
              <span class="opacity-60"> ${label} </span>
              <span class="font-semibold">${value}</span>
            </div>
          `;
          })
          .join('')}
      </div>
    `;
  }

  private createMarkerIcon(total: number, counts?: IMapItemCounts) {
    const states = counts
      ? (Object.entries(counts) as [keyof IMapItemCounts, number][])
          .filter(([, count]) => count > 0)
          .reduce((acc, [key, count]) => {
            acc[key] = count / total;
            return acc;
          }, {} as Record<keyof IMapItemCounts, number>)
      : null;

    if (states) {
      const MIN_FRACTION_THRESHOLD = 0.05;

      for (const [state, fraction] of Object.entries(states)) {
        if (fraction < MIN_FRACTION_THRESHOLD) {
          const diff = MIN_FRACTION_THRESHOLD - fraction;
          const biggestFractionKey = Object.entries(states).reduce(
            (acc, [key, value]) => {
              return value > acc.value ? { key, value } : acc;
            },
            { key: '', value: 0 },
          ).key;

          states[state as keyof IMapItemCounts] = MIN_FRACTION_THRESHOLD;
          states[biggestFractionKey as keyof IMapItemCounts] =
            states[biggestFractionKey as keyof IMapItemCounts] - diff;
        }
      }
    }

    const markerClasses = [
      'map-marker',
      total > 1 ? 'map-marker-cluster' : 'map-marker-single',
      // ...states.map((state) => 'map-marker-' + state),
    ];

    const radius = 50;
    const strokeWidth = 15;
    const circumference = 2 * Math.PI * radius;
    const size = radius * 2 + strokeWidth;

    const createBorderCircle = (
      state: keyof IMapItemCounts | 'unknown',
      fraction = 1,
      index = 0,
    ) => {
      const circumferenceFraction = circumference * fraction;

      return `<circle
        cx="${size / 2}"
        cy="${size / 2}"
        r="${radius}"
        class="map-marker-circle map-marker-circle-${state}"
        stroke-dasharray="${circumferenceFraction} ${
        circumference - circumferenceFraction
      }"
        stroke-dashoffset="${index * circumferenceFraction}"
        stroke-width="${strokeWidth}"
      />`;
    };

    return L.divIcon({
      html: `
        <div class="map-marker-inner">
          <svg viewBox="0 0 ${size} ${size}" class="map-marker-icon">
            ${
              states
                ? Object.entries(states)
                    .map(([state, fraction], i) => {
                      return createBorderCircle(
                        state as keyof IMapItemCounts,
                        fraction,
                        i,
                      );
                    })
                    .join('')
                : createBorderCircle('unknown')
            }
          </svg>
          <span class="map-marker-text">
            ${total}
          </span>
        </div>
        `,
      className: markerClasses.join(' '),
      iconSize: L.point(60, 60),
    });
  }

  private isOpticalMarker(marker: L.Marker): marker is L.Marker<IMapItem> {
    return (
      marker?.feature !== undefined &&
      (marker?.feature?.properties as IMapItem)?.count !== undefined
    );
  }

  constructor() {
    effect(() => this.refreshMarkers(this.items()));

    effect(() => {
      const coords = this.selectedItem();

      if (coords?.gpsX && coords?.gpsY) {
        this.map.setView([coords.gpsY, coords.gpsX], this.map.getMaxZoom());
      }
    });
  }
}
